/* Intro loader: holds briefly, then flies the fabric swatches off-frame
 * while the center logo shrinks/translates into the header logo's real
 * on-screen position. Runs once per browser session. */

(function () {
  const STORAGE_KEY = 'hofLoaderSeen';

  function markSeen() {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (error) {
      // sessionStorage unavailable (private mode, etc.) - nothing to persist.
    }
  }

  function alreadySeen() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  class SiteLoader extends HTMLElement {
    connectedCallback() {
      // Replay on every load in the theme editor so the hold duration can be tuned.
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

      if (!this.designMode && alreadySeen()) {
        this.remove();
        return;
      }

      this.swatches = Array.from(this.querySelectorAll('.site-loader__swatch'));
      this.logoCard = this.querySelector('.site-loader__logo-card');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.exiting = false;

      document.body.classList.add('site-loader-active');

      const startHold = () => {
        const holdDuration = parseInt(this.dataset.holdDuration, 10);
        const hold = Number.isNaN(holdDuration) ? 1100 : holdDuration;
        this.holdTimer = window.setTimeout(() => this.exit(), this.reduceMotion ? Math.min(hold, 250) : hold);
      };

      // Wait for web fonts before starting the countdown (capped, so a slow
      // or failed font load can't hang the loader). The header logo's target
      // position/size is measured from live layout right when we exit - if
      // the heading font swaps in *after* that measurement (a plain FOUT),
      // the header text reflows and the shrink lands on stale coordinates,
      // which is what makes the landing look off.
      if (document.fonts && document.fonts.ready) {
        Promise.race([document.fonts.ready, new Promise((resolve) => window.setTimeout(resolve, 1500))]).then(
          startHold
        );
      } else {
        startHold();
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.holdTimer);
      window.clearTimeout(this.finishTimer);
      // Never leave the header logo hidden because we were torn down mid-flight.
      this.revealHeaderLogo();
    }

    exit() {
      if (this.exiting) return;
      this.exiting = true;

      const flying = !this.reduceMotion && this.flyLogoToHeader();

      if (!this.reduceMotion) this.flySwatchesOutward();

      // Nothing to hand the logo over to (text logo, or reduced motion), so let
      // it fade where it stands rather than sit there and vanish on removal.
      if (!flying) this.classList.add('site-loader--logo-fade');

      this.classList.add('site-loader--exit');

      // All three exit transitions in loader.css now land together at 1.1s —
      // the swatches are gone, the shell has faded to transparent, and the logo
      // arrives exactly on top of the header logo. The extra beat is just slack
      // so the swap happens after the transform has settled, never during it.
      const totalDuration = this.reduceMotion ? 350 : 1150;
      this.finishTimer = window.setTimeout(() => this.finish(), totalDuration);
    }

    // Measures the header logo and points the lockup at it. Returns false when
    // there is no usable target, so the caller can fall back to a plain fade.
    flyLogoToHeader() {
      const targetEl = document.querySelector('.header__heading-logo, .header__heading-link .h2');
      if (!this.logoCard || !targetEl) return false;

      const from = this.logoCard.getBoundingClientRect();
      const to = targetEl.getBoundingClientRect();
      if (!from.width || !to.width) return false;

      const scale = to.width / from.width;
      const fromCenterX = from.left + from.width / 2;
      const fromCenterY = from.top + from.height / 2;
      const toCenterX = to.left + to.width / 2;
      const toCenterY = to.top + to.height / 2;

      this.logoCard.style.setProperty('--logo-fly-x', `${toCenterX - fromCenterX}px`);
      this.logoCard.style.setProperty('--logo-fly-y', `${toCenterY - fromCenterY}px`);
      this.logoCard.style.setProperty('--logo-fly-scale', scale.toFixed(4));
      // Pre-scale size for the header-logo copy: at the card's own fly-scale
      // (to.width / from.width) a child laid out at from.width renders at
      // exactly to.width, so it settles on the header logo pixel for pixel.
      this.logoCard.style.setProperty('--logo-swap-width', `${from.width}px`);

      // The header logo is revealed by the shell fading away underneath the
      // flying lockup, which means both are on screen at once for the length of
      // the flight — the real one sitting in the header while its double is
      // still travelling towards it. Hold it back and let it appear only at the
      // handover, so the arriving logo *becomes* the header logo.
      this.headerLogo = targetEl;
      this.headerLogoVisibility = targetEl.style.visibility;
      targetEl.style.visibility = 'hidden';

      return true;
    }

    revealHeaderLogo() {
      if (!this.headerLogo) return;
      this.headerLogo.style.visibility = this.headerLogoVisibility || '';
      this.headerLogo = undefined;
    }

    flySwatchesOutward() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const centerX = vw / 2;
      const centerY = vh / 2;
      const diagonal = Math.sqrt(vw * vw + vh * vh);

      this.swatches.forEach((swatch) => {
        const rect = swatch.getBoundingClientRect();
        const swatchCenterX = rect.left + rect.width / 2;
        const swatchCenterY = rect.top + rect.height / 2;

        let dx = swatchCenterX - centerX;
        let dy = swatchCenterY - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        dx = (dx / distance) * diagonal;
        dy = (dy / distance) * diagonal;

        const rotation = (Math.random() - 0.5) * 60;
        // Capped so the last swatch (delay + 0.95s transition) still clears the
        // frame before the 1.15s removal, instead of being cut off mid-flight.
        const delay = Math.random() * 120;

        swatch.style.setProperty('--fly-x', `${dx}px`);
        swatch.style.setProperty('--fly-y', `${dy}px`);
        swatch.style.setProperty('--fly-rot', `${rotation}deg`);
        swatch.style.transitionDelay = `${delay}ms`;
      });
    }

    finish() {
      if (!this.designMode) markSeen();
      document.body.classList.remove('site-loader-active');
      // Same frame as hiding the lockup: the flying logo has landed on the
      // header logo's exact box, so swapping one for the other is invisible.
      this.revealHeaderLogo();
      this.setAttribute('hidden', '');
      this.remove();
    }
  }

  if (!customElements.get('site-loader')) {
    customElements.define('site-loader', SiteLoader);
  }
})();
